import { useEffect, useState } from "react";

import { useToast } from "@hightouch/htui";

import {
  type SyncRunsQuery,
  useCancelSyncRunMutation,
  useStartClearAndFillRunMutation,
  useStartResetCdcRunMutation,
  useStartSyncRunMutation,
  useSyncRunsForSyncsQuery,
  useSyncRunsQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { SyncRunStatus, syncStatusIsTerminal } from "src/utils/syncs";
import { captureException } from "src/lib/monitoring";

export type SyncRun = SyncRunsQuery["sync_requests"][0] | undefined;

export const isSyncRunActive = (run: SyncRun): boolean =>
  run?.sync_attempts?.[0]?.status === SyncRunStatus.ACTIVE;

/** Fields that, when unchanged, mean a run-status poll can back off. */
const syncRunRefetchKey = (run: SyncRun) => [
  run?.id,
  run?.status,
  run?.finished_at,
  run?.sync_attempts?.[0]?.status,
];

export type SyncRunActions = {
  isRunning: boolean;
  isCanceling: boolean;
  isRunDisabled: boolean;
  startRun: (options?: {
    resync?: boolean;
    reset_cdc?: boolean;
    clear_and_fill?: boolean;
  }) => Promise<void>;
  cancelRun: () => Promise<void>;
};

/** The subset a list row needs to render + drive its run button. */
export type SyncRunControls = Pick<
  SyncRunActions,
  "isRunning" | "isRunDisabled" | "startRun"
>;

/**
 * Run/cancel mutations + run state for a single sync, derived from a `latestRun`
 * the caller owns — so the run-status query can be batched across many syncs.
 * Mutations rely on the global mutation `onSuccess` (see lib/react-query) to
 * invalidate and refetch run-status queries.
 */
export const useSyncRunActions = (
  id: string | undefined,
  latestRun: SyncRun,
): SyncRunActions => {
  const [isRunDisabled, setIsRunDisabled] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [canceledSyncId, setCanceledSyncId] = useState(false);

  const { mutateAsync: cancelSyncRequest } = useCancelSyncRunMutation();
  const { mutateAsync: forceRun } = useStartSyncRunMutation();
  const { mutateAsync: forceResetCDCSyncRun } = useStartResetCdcRunMutation();
  const { mutateAsync: forceClearAndFillSyncRun } =
    useStartClearAndFillRunMutation();

  const { toast } = useToast();

  const syncAttempt = latestRun?.sync_attempts?.[0];

  useEffect(() => {
    // if the latest run id no longer match the canceled id then it means cancel is successful.
    if (
      syncAttempt?.status === SyncRunStatus.CANCELED ||
      canceledSyncId !== latestRun?.id
    ) {
      setIsCanceling(false);
    }
  }, [syncAttempt?.status, latestRun?.id, canceledSyncId]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (syncStatusIsTerminal(syncAttempt?.status as SyncRunStatus)) {
      setIsRunDisabled(false);
    }
  }, [syncAttempt?.id, syncAttempt?.status]);

  const cancelRun = async (): Promise<void> => {
    setIsCanceling(true);
    setCanceledSyncId(latestRun?.id);
    try {
      await cancelSyncRequest({
        id: latestRun?.destination_instance_id,
        syncRequestId: latestRun?.id,
      });
    } catch (error) {
      toast({
        id: "cancel-failed",
        title: "Failed to cancel sync",
        variant: "error",
      });
      captureException(error);
    }
  };

  const startRun = async ({
    resync = false,
    reset_cdc = false,
    clear_and_fill = false,
  }: {
    resync?: boolean;
    reset_cdc?: boolean;
    clear_and_fill?: boolean;
  } = {}): Promise<void> => {
    setIsRunDisabled(true);
    let startSyncRun;
    try {
      if (reset_cdc) {
        ({ startSyncRun } = await forceResetCDCSyncRun({
          id: Number(id),
          reset_cdc: reset_cdc,
        }));
      } else if (clear_and_fill) {
        ({ startSyncRun } = await forceClearAndFillSyncRun({
          id: Number(id),
          clear_and_fill: clear_and_fill,
        }));
      } else {
        ({ startSyncRun } = await forceRun({
          id: Number(id),
          full_resync: resync,
        }));
      }
    } catch (error) {
      // No attempt started, so the terminal-status effect would never re-enable the button.
      setIsRunDisabled(false);
      throw error;
    }
    // Only render a popup message if we actually scheduled a sync.
    const scheduled = startSyncRun?.scheduled;

    if (scheduled) {
      toast({
        id: "start-sync-run",
        title: resync
          ? "Resync will begin shortly"
          : "Manual run will begin shortly",
        variant: "success",
      });
    }
  };

  return {
    isRunning: isSyncRunActive(latestRun),
    isCanceling,
    isRunDisabled,
    startRun,
    cancelRun,
  };
};

export const useCurrentRun = (
  id: string | undefined,
): { latestRun: SyncRun } & SyncRunActions => {
  const { refetchInterval } = useGovernedRefetchInterval<SyncRun>({
    baseMs: 5000,
    compareKey: syncRunRefetchKey,
  });

  const { data: latestRun } = useSyncRunsQuery<SyncRun>(
    {
      filter: {
        destination_instance_id: { _eq: id?.toString() },
        // We only want to use the status of the last run that is not queued,
        // because a run can have at most one active attempt and one queued attempt
        status: { _neq: "queued" },
      },
      limit: 1,
    },
    {
      enabled: Boolean(id),
      select: (data) => data.sync_requests?.[0],
      refetchInterval,
      notifyOnChangeProps: "tracked",
      keepPreviousData: true,
    },
  );

  return { latestRun, ...useSyncRunActions(id, latestRun) };
};

/**
 * One batched, governed-backoff poll of latest run status for a set of syncs,
 * keyed by sync id. Lets a list share a single run-status query across rows
 * instead of each row calling useCurrentRun. Run/cancel mutations refresh it
 * via the global mutation `onSuccess` invalidation (see lib/react-query).
 */
export const useSyncRunsForSyncs = (
  ids: string[],
): { runBySyncId: Map<string, SyncRun> | undefined } => {
  const { refetchInterval } = useGovernedRefetchInterval<SyncRun[]>({
    baseMs: 5000,
    compareKey: (runs) => (runs ?? []).map(syncRunRefetchKey),
  });

  const { data: runBySyncId } = useSyncRunsForSyncsQuery(
    { ids },
    {
      enabled: ids.length > 0,
      select: (data) =>
        new Map(
          data.sync_requests.map((run) => [
            String(run.destination_instance_id),
            run,
          ]),
        ),
      refetchInterval: (data, query) =>
        refetchInterval(data ? Array.from(data.values()) : undefined, query),
      notifyOnChangeProps: "tracked",
      keepPreviousData: true,
    },
  );

  return { runBySyncId };
};
